import React from 'react';
import { HelpCircle, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { HelpTerm, findTerm } from '../../config/helpContent';
import { InfoTooltip } from './InfoTooltip';

interface Breadcrumb {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  description?: string; // Alias para subtitle
  helpTerms?: string[]; // Lista de termos para mostrar tooltips
  helpContent?: string; // Conteúdo de ajuda em markdown
  breadcrumbs?: Breadcrumb[];
  actions?: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  module?: string;
}

/**
 * Cabeçalho de página com suporte a ajuda contextual
 */
export function PageHeader({
  title,
  subtitle,
  description,
  helpTerms = [],
  helpContent,
  breadcrumbs,
  actions,
  className,
  icon,
  module,
}: PageHeaderProps) {
  // Resolve os termos de ajuda
  const resolvedTerms = helpTerms
    .map(t => findTerm(t))
    .filter(Boolean) as HelpTerm[];

  // Use description como fallback para subtitle
  const displaySubtitle = subtitle || description;

  return (
    <div className={cn('mb-6', className)}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-1 text-sm text-surface-400 mb-2">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <ChevronRight className="w-4 h-4" />}
              {crumb.href ? (
                <Link
                  to={crumb.href}
                  className="hover:text-primary-400 transition-colors"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-surface-300">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Title Row */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {icon && <span className="text-primary-400">{icon}</span>}
            <h1 className="text-2xl font-bold text-white">{title}</h1>
            
            {/* Help Terms Badges */}
            {resolvedTerms.length > 0 && (
              <div className="flex items-center gap-1">
                {resolvedTerms.slice(0, 3).map((term) => (
                  <InfoTooltip key={term.term} term={term} position="bottom">
                    <span className="text-xs px-2 py-0.5 bg-surface-800 border border-surface-700 rounded-full text-surface-400 hover:text-primary-400 hover:border-primary-500/50 transition-colors cursor-help">
                      {term.term}
                    </span>
                  </InfoTooltip>
                ))}
                {resolvedTerms.length > 3 && (
                  <span className="text-xs px-2 py-0.5 bg-surface-800 border border-surface-700 rounded-full text-surface-500">
                    +{resolvedTerms.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {displaySubtitle && (
            <p className="text-surface-400">{displaySubtitle}</p>
          )}
          
          {module && (
            <span className="text-xs px-2 py-0.5 bg-primary-500/20 text-primary-400 rounded">
              {module}
            </span>
          )}
        </div>

        {/* Actions */}
        {actions && (
          <div className="flex items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Componente para destacar um termo com tooltip de ajuda inline
 */
interface HelpTermInlineProps {
  term: string;
  children?: React.ReactNode;
  className?: string;
}

export function HelpTermInline({ term, children, className }: HelpTermInlineProps) {
  const helpTerm = findTerm(term);
  
  if (!helpTerm) {
    return <span className={className}>{children || term}</span>;
  }

  return (
    <InfoTooltip term={helpTerm} className={className}>
      <span className="border-b border-dashed border-surface-500 cursor-help">
        {children || term}
      </span>
    </InfoTooltip>
  );
}

export default PageHeader;
